import React, { useEffect, useState } from "react";
import HeatMapGrid from "react-heatmap-grid";
import styles from "./ActivityChart.module.css";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import useWindowSize from "@hooks/useWindowSize";
import { parse, set } from 'date-fns';

const xLabels = ["S", "M", "T", "W", "T", "F", "S"];
const yLabels = ["", "", "", "", "", ""]; 

const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const getCellColor = (frequency) => {
  if (!frequency) return "#fff";
  if (frequency >= 10) return "#007aff";
  if (frequency >= 7) return "#40a9ff";
  if (frequency >= 4) return "#90cfff";
  if (frequency >= 1) return "#cce6ff";
  return "#f0f0f0";
};

const ActivityChart = (activityData) => {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  
  const {width} = useWindowSize();

  const frequencyMap = {};

  if (Array.isArray(activityData.data)) {
    activityData.data.forEach(dateStr => {
      if (!dateStr || dateStr.toLowerCase().includes("unknown")) return;
    
      const parsed = parse(dateStr, 'dd/MM/yyyy', new Date());
    
      const key = `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}-${String(parsed.getDate()).padStart(2, "0")}`;
    
      frequencyMap[key] = (frequencyMap[key] || 0) + 1;
    });
  }

  const getDaysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
  const getStartDayOfWeek = (month, year) => new Date(year, month, 1).getDay();

  const generateCalendarData = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const startDay = getStartDayOfWeek(currentMonth, currentYear);
    const calendar = [];
    let currentDate = 1;

    for (let week = 0; week < 6; week++) {
      const row = [];
      for (let day = 0; day < 7; day++) {
        const cellIndex = week * 7 + day;
        if (cellIndex >= startDay && currentDate <= daysInMonth) {
          row.push(currentDate++);
        } else {
          row.push(null);
        }
      }
      calendar.push(row);
    }
    return calendar;
  };

  const handleMonthChange = (delta) => {
    let newMonth = currentMonth + delta;
    let newYear = currentYear;

    if (newMonth > 11) {
      newMonth = 0;
      newYear += 1;
    } else if (newMonth < 0) {
      newMonth = 11;
      newYear -= 1;
    }

    setCurrentMonth(newMonth);
    setCurrentYear(newYear);
  };

  // Responsive sizes
  const [cellWidth, setCellWidth] = useState(50);
  const [cellHeight, setCellHeight] = useState(44.29);
  const [fontSize,setFontSize] = useState("0.8rem");
  const [margin,setMargin] = useState("0.5rem 2.5rem");

  const calendar = generateCalendarData();
  const yearsRange = Array.from({ length: 21 }, (_, i) => currentYear - 10 + i);

  return (
    <div className={styles.heatmapContainer}>
      <div className={styles.navigation}>
        <div className={styles.specificNavigation}>
          <button onClick={() => handleMonthChange(-1)}>&lt;</button>
          <div className={styles.dropdownWrapper}>
            <span onClick={() => setShowMonthDropdown(!showMonthDropdown)}>
              {months[currentMonth]}<ArrowDropDownIcon className={styles.dropdownIcon} />
            </span>
            {showMonthDropdown && (
              <div className={styles.dropdown}>
                {months.map((month, index) => (
                  <div
                    key={month}
                    onClick={() => {
                      setCurrentMonth(index);
                      setShowMonthDropdown(false);
                    }}
                  >
                    {month}
                  </div>
                ))}
              </div>
            )}
          </div>
          <button onClick={() => handleMonthChange(1)}>&gt;</button>
        </div>

        <div className={styles.specificNavigation}>
          <button onClick={() => handleMonthChange(-1)}>&lt;</button>
          <div className={styles.dropdownWrapper}>
            <span onClick={() => setShowYearDropdown(!showYearDropdown)}>
              {currentYear}<ArrowDropDownIcon className={styles.dropdownIcon} />
            </span>
            {showYearDropdown && (
              <div className={styles.dropdown}>
                {yearsRange.map((year) => (
                  <div
                    key={year}
                    onClick={() => {
                      setCurrentYear(year);
                      setShowYearDropdown(false);
                    }}
                  >
                    {year}
                  </div>
                ))}
              </div>
            )}
          </div>

          <button onClick={() => handleMonthChange(1)}>&gt;</button>
        </div>
      </div>

      <div className={styles.scrollContainer}>
        <HeatMapGrid
          xLabels={xLabels}
          yLabels={yLabels}
          data={calendar}
          cellStyle={(background, value) => {
            const key = value
              ? `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(value).padStart(2, "0")}`
              : null;
              const frequency = frequencyMap[key];

              if (width < 1400) {
                setFontSize("0.7rem");
                setMargin("0.5rem 1.5rem");
              }
              if (width < 1150) {
                setMargin("0.4rem .5rem");
              }
              if (width < 921) {
                setCellHeight(49.15);
                setMargin("0.5rem 1rem");
              }
              if (width < 750) {
                setCellWidth("54.87")
                setMargin("0.4rem 0.5rem");
              }
              if (width < 620) {
                setCellWidth("44.87")
                setMargin("0.4rem 0.5rem");
              }

            return {
              background: getCellColor(frequency),
              borderRadius: "50%",
              color: "#000",
              width: `${cellWidth}px`,
              height: `${cellHeight}px`,
              margin: margin,
              display: "flex",
              alignItems: "center",
              paddingBottom: ".5rem",
              justifyContent: "center",
              fontSize: fontSize,
            };
          }}
          cellRender={(value) => (value ? value.toString().padStart(2, "0") : "")}
        />
      </div>

      <div className={styles.legendContainer}>
        <p>It is prescribed to exercise 10 mins daily</p>
        <div className={styles.legend}>
          <span><span className={styles.dotLevel4}></span> &lt;10 Times</span>
          <span><span className={styles.dotLevel3}></span> &lt;7 Times</span>
          <span><span className={styles.dotLevel2}></span> &lt;4 Times</span>
          <span><span className={styles.dotLevel1}></span> &lt;2 Times</span>
        </div>
      </div>
    </div>
  );
};

export default ActivityChart;